import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./UnseenPaywall.module.scss"

/**
 * UnseenPaywall (`/unseen`) — an original, self-contained NON-ONBOARDING upsell
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * NON-ONBOARDING.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — but through a fresh
 * privacy lens: not another lock, door or watchman, but the quiet problem of
 * SCATTER. Your memories pile up on every ordinary, glanceable surface —
 * Camera Roll, Recents, a shared album, the cloud stream — anywhere a thumb
 * can scroll. Premium gathers every one into a single private place that opens
 * only for you.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Everywhere anyone can scroll — or one place only you open." The hero is a
 * loose FIELD of your real memories strewn across the top, each tagged with the
 * ordinary surface it's exposed on (Camera Roll · Shared · Recents · Cloud …).
 * Below sits your private VAULT, empty and waiting. TAKE THEM PRIVATE — throw
 * the one master switch and every scattered memory lifts off its public surface
 * and travels home into the vault, settling into a tidy private grid; the
 * surface tags fade, the vault seals, and the four real safeguards light one by
 * one. The value isn't fear — it's the exhale of everything gathered in one
 * private place that's yours alone.
 *
 * ── Divergence (vs. the 150+ prior concepts, incl. the security cluster) ────
 * Deliberately far from every lock/key/door/watch neighbour (Static/Cipher
 * consoles, Eyes Only dossier, Safelight darkroom, Ward crest, Sanctum rings,
 * OnlyYou keypad, Trace pattern, Imprint fingerprint, Vault Door, Reissue
 * card-reader, Shade blind, Bystander dual-reality, PlainSight disguise,
 * Strongbox wall, Clasp diary, Sentry night-watch, Nightlatch, Perimeter,
 * Facedown, Latent, Watermark, Aperture):
 * Layout      : a SCATTER FIELD over a receiving VAULT — a before/after spatial
 *               model of "loose everywhere" vs "gathered in one place". Not a
 *               single hero object, not a lockscreen, not a wall/door/console.
 * Interaction : TAKE-THEM-PRIVATE — one master throw that sweeps every strewn
 *               memory home. A gather/consolidate verb, new to the set (not
 *               zip, haul, buckle, arm-toggle, feed-card, keypad, dial, trace).
 * Metaphor    : SCATTER → ONE PRIVATE PLACE. Exposure as surface-clutter, not a
 *               thief at the door. Privacy framed as gathering, not fortifying.
 * Motion      : memories flying off their public surfaces along curved paths
 *               into a settling grid; surface tags dissolving; the vault sealing
 *               with a soft private bloom; safeguards igniting one by one.
 * Feature     : the four real protections presented as "what 'private' now
 *               means", lighting as the vault fills.
 * Value frame : CONTRAST + ENDOWMENT + order — these memories are already yours
 *               and already everywhere; gather them into the one place only you
 *               open. Calm relief, not dread.
 * CTA         : COMMITMENT — "Keep them private for a year"; yearly featured.
 * Register    : calm, exhaling, domestic — coming home, not standing guard.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock
 * with Face ID / Touch ID / code · encrypted Cloud Vault · Break-in reports ·
 * App disguise), and keeping photos out of the Camera Roll is the app's core
 * function. The scatter field is an honest illustration of where phone photos
 * ordinarily live; nothing here claims the user has actually been snooped on.
 * This is a non-onboarding upsell, so nothing is at stake and nothing is
 * deleted — "taking them private" MOVES sample memories into the vault; the copy
 * states plainly that photos are gathered, never lost, and stay yours. No
 * countdown, no fabricated scarcity, no data-loss threat. "Save 79%" is real
 * arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small and secondary. The 7-day free trial is the
 * real trial. Renders on built-in debug product data with bundled photos — no
 * native injection, no external egress.
 */

// The real Private Photo Vault safeguards — what "private" means once the
// memories are gathered. Each lights (in order) as the vault seals.
const GUARDS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID, or a code only you know",
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of every memory",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in reports",
    line: "a silent photo of anyone who tries the lock",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "the vault can pass for an ordinary app",
  },
]

// Sample memories, strewn across the ordinary surfaces they'd normally sit on.
// Bundled, self-contained photos reused from the repo's memory set. `s`/`t`/`r`
// are the scatter position (left% / top% / rotation) in the field; `on` is the
// public surface the memory is tagged with; `v` is its final slot in the vault
// grid (0-based, filled left→right, top→bottom).
const MEMORIES = [
  { src: "/unseen/m1.jpg", s: 4, t: 1, r: -10, on: "Camera Roll", v: 0 },
  { src: "/unseen/m2.jpg", s: 43, t: 3, r: 9, on: "Shared album", v: 1 },
  { src: "/unseen/m3.jpg", s: 74, t: 0, r: 12, on: "Cloud", v: 2 },
  { src: "/unseen/m4.jpg", s: 20, t: 15, r: 5, on: "Recents", v: 3 },
  { src: "/unseen/m5.jpg", s: 58, t: 16, r: -8, on: "Camera Roll", v: 4 },
  { src: "/unseen/m6.jpg", s: 3, t: 18, r: 8, on: "AirDrop", v: 5 },
  { src: "/unseen/m7.jpg", s: 77, t: 15, r: -6, on: "Recents", v: 6 },
  { src: "/unseen/m8.jpg", s: 40, t: 19, r: 13, on: "Shared album", v: 7 },
]

// Vault grid: 4 columns × 2 rows. Returns the top-left (left%/top%) of slot `v`.
const VAULT_COLS = 4
const slotPosition = (v: number) => {
  const col = v % VAULT_COLS
  const row = Math.floor(v / VAULT_COLS)
  const left = 8.5 + col * 21 // photos are 18.5% wide → right edge ~90%
  const top = 48 + row * 23
  return { left, top }
}

// The public surfaces the memories are pulled off of — named back in the
// confirmation once they're gathered. Honest: keeping photos out of the Camera
// Roll (and off shared/recents surfaces) is the vault's core function.
const CLEARED_FROM = ["Camera Roll", "Shared albums", "Recents"]

const UnseenPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  const dark = state.initialData?.darkMode
  // Warm the safe-area insets to the stage so it reads as one calm surface.
  useEffect(() => {
    const inset = dark ? "#12151d" : "#eef1f7"
    actions.setHeaderInsetColor(inset)
    actions.setFooterInsetColor(inset)
  }, [actions, dark])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the master switch: false = scattered / exposed, true = gathered private ─
  const [isPrivate, setIsPrivate] = useState(false)
  const userActed = useRef(false)

  const toggle = useCallback(() => {
    userActed.current = true
    setIsPrivate((p) => !p)
  }, [])

  // Auto-demo the gather once on arrival so the payoff reads at a glance.
  useEffect(() => {
    if (reduceMotion) {
      setIsPrivate(true)
      return
    }
    const id = window.setTimeout(() => {
      if (!userActed.current) setIsPrivate(true)
    }, 900)
    return () => window.clearTimeout(id)
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div className={`${styles.root} ${isPrivate ? styles.isPrivate : ""}`}>
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            Your memories, everywhere
          </span>
          <h1 className={styles.title}>
            Seen everywhere,
            <br />
            or <span className={styles.titleAccent}>unseen</span> by everyone.
          </h1>
          <p className={styles.sub}>
            Right now your memories sit on every surface a thumb can scroll.
            Premium gathers every one into a single private place that opens
            only for you.
          </p>
        </section>

        {/* ── the field + vault (hero) ───────────────────────────────────── */}
        <section className={styles.stageWrap}>
          <button
            className={styles.stage}
            onClick={toggle}
            aria-pressed={isPrivate}
            aria-label={
              isPrivate
                ? "Release the memories back to their public surfaces"
                : "Take the memories private"
            }
          >
            {/* the receiving vault behind the field */}
            <div className={styles.vault} aria-hidden="true">
              <div className={styles.vaultRim} />
              <div className={styles.vaultLip}>
                <span className={styles.vaultLabel}>
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <rect
                      x="5"
                      y="10.5"
                      width="14"
                      height="9.5"
                      rx="2.2"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    />
                    <path
                      className={styles.vaultShackle}
                      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                  </svg>
                  Your vault
                  <span className={styles.vaultOnly}>only you</span>
                </span>
                <span className={styles.vaultCount}>
                  {isPrivate ? `${MEMORIES.length} inside` : "empty"}
                </span>
              </div>
              <div className={styles.vaultSheen} />
            </div>

            {/* confirmation that fills the freed space once gathered */}
            <div className={styles.cleared} aria-hidden="true">
              <span className={styles.clearedMark}>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.clearedTitle}>
                Off every ordinary surface
              </span>
              <span className={styles.clearedTags}>
                {CLEARED_FROM.map((c) => (
                  <span key={c} className={styles.clearedTag}>
                    {c}
                  </span>
                ))}
              </span>
            </div>

            {/* faint receiving slots in the empty vault — perfectly aligned to
                where each memory lands, so the vault reads as "waiting" */}
            <div className={styles.slotsLayer} aria-hidden="true">
              {MEMORIES.map((m, i) => {
                const slot = slotPosition(m.v)
                return (
                  <span
                    key={i}
                    className={styles.slot}
                    style={
                      {
                        ["--vl"]: `${slot.left}%`,
                        ["--vt"]: `${slot.top}%`,
                      } as React.CSSProperties
                    }
                  />
                )
              })}
            </div>

            {/* the scattered memories */}
            <div className={styles.field}>
              {MEMORIES.map((m, i) => {
                const slot = slotPosition(m.v)
                return (
                  <span
                    key={i}
                    className={styles.memory}
                    style={
                      {
                        ["--sl"]: `${m.s}%`,
                        ["--st"]: `${m.t}%`,
                        ["--sr"]: `${m.r}deg`,
                        ["--vl"]: `${slot.left}%`,
                        ["--vt"]: `${slot.top}%`,
                        ["--d"]: i,
                      } as React.CSSProperties
                    }
                  >
                    <span
                      className={styles.memoryPhoto}
                      style={{ backgroundImage: `url(${m.src})` }}
                    />
                    <span className={styles.surfaceTag}>{m.on}</span>
                    <span className={styles.memoryGloss} aria-hidden="true" />
                  </span>
                )
              })}
            </div>
          </button>

          {/* status line under the stage */}
          <span
            className={`${styles.status} ${isPrivate ? styles.statusOn : ""}`}
            aria-live="polite"
          >
            <span className={styles.statusDot} aria-hidden="true" />
            {isPrivate
              ? "Gathered · in your private vault only"
              : "Scattered · across Camera Roll, Shared & more"}
          </span>

          {/* the single decisive affordance — the master switch */}
          <button
            className={`${styles.master} ${isPrivate ? styles.masterOn : ""}`}
            onClick={toggle}
            aria-pressed={isPrivate}
          >
            <span className={styles.masterTrack}>
              <span className={styles.masterThumb} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  {isPrivate ? (
                    <>
                      <rect
                        x="5"
                        y="10.5"
                        width="14"
                        height="9.5"
                        rx="2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                      />
                      <path
                        d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                      />
                    </>
                  ) : (
                    <>
                      <rect
                        x="5"
                        y="10.5"
                        width="14"
                        height="9.5"
                        rx="2"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                      />
                      <path
                        d="M8 10.5V8a4 4 0 0 1 7.7-1.4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                      />
                    </>
                  )}
                </svg>
              </span>
            </span>
            <span className={styles.masterLabel}>
              {isPrivate
                ? "In your vault — tap to release"
                : "Take them private"}
            </span>
          </button>
        </section>

        {/* ── what "private" now means (real safeguards) ─────────────────── */}
        <ul className={styles.guards}>
          <li className={styles.guardsHead}>
            <span>What "private" now means</span>
            <span className={styles.guardsCount}>
              {isPrivate
                ? `${GUARDS.length}/${GUARDS.length}`
                : `0/${GUARDS.length}`}{" "}
              on
            </span>
          </li>
          {GUARDS.map((g, i) => (
            <li
              key={g.key}
              className={`${styles.guard} ${isPrivate ? styles.guardOn : ""}`}
              style={{ ["--gd"]: i } as React.CSSProperties}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <g.Icon width={16} height={16} />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardChip} aria-hidden="true">
                {isPrivate ? (
                  <svg viewBox="0 0 24 24" width="12" height="12">
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  "•"
                )}
              </span>
            </li>
          ))}
        </ul>

        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Taking them private only gathers your memories — it never loses them.
          Every photo stays yours, kept out of your Camera Roll and encrypted in
          your Cloud Vault, opened by your Face&nbsp;ID or code alone.
        </p>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Private all year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or month-to-month</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Keep them private — start ${trialDays}-day free trial`
              : "Keep them private"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default UnseenPaywall
